import React, { useCallback, useState } from 'react'
import Home from './components/Home'
import Logger from './components/Logger'
import History from './components/History'
import Settings from './components/Settings'
import Chat from './components/Chat'
import type { UserAccount, UserPayload, UserRole, UserUpdatePayload } from './types'
import { TrackerProvider, useTracker, type UserDemographics } from './context/TrackerContext'
import usersJson from './users.json'
import { fetchUsersList, createUserAccount, deleteUserAccount, updateUserAccount, recordUserLogin } from './storage'
import { hashPassword } from './utils/password'

type Route =
  | { name: 'home' }
  | { name: 'logger' }
  | { name: 'history' }
  | { name: 'settings' }
  | { name: 'chat' }

type SessionUser = { username: string; role: UserRole }
const SESSION_KEY = 'my-tracker-session:v1'
const defaultUsers = sortUsersByRecency(usersJson as UserAccount[])
const APPLE_LOGIN_URL = import.meta.env.VITE_APPLE_LOGIN_URL ?? ''
const GOOGLE_LOGIN_URL = import.meta.env.VITE_GOOGLE_LOGIN_URL ?? ''

function getStoredUser(): SessionUser | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SessionUser
    return parsed?.username ? parsed : null
  } catch {
    return null
  }
}

export default function App() {
  const [userOptions, setUserOptions] = useState<UserAccount[]>(defaultUsers)
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() => getStoredUser())
  const [loadingUsers, setLoadingUsers] = useState(false)

  const persistSession = useCallback((user: SessionUser | null) => {
    if (typeof window === 'undefined') return
    if (user) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(user))
    } else {
      localStorage.removeItem(SESSION_KEY)
    }
  }, [])

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true)
    try {
      const remote = await fetchUsersList()
      setUserOptions(sortUsersByRecency(remote))
      if (sessionUser) {
        const current = remote.find(u => u.username === sessionUser.username)
        if (!current) {
          persistSession(null)
          setSessionUser(null)
        } else if (current.role !== sessionUser.role) {
          const updated = { username: current.username, role: current.role }
          setSessionUser(updated)
          persistSession(updated)
        }
      }
    } catch {
      setUserOptions(defaultUsers)
    } finally {
      setLoadingUsers(false)
    }
  }, [persistSession, sessionUser])

  React.useEffect(() => {
    loadUsers()
  }, [loadUsers])

  function handleLogin(user: SessionUser) {
    setSessionUser(user)
    persistSession(user)
    recordUserLogin(user.username)
  }

  function handleLogout() {
    persistSession(null)
    setSessionUser(null)
  }

  async function addUser(user: UserPayload) {
    await createUserAccount(user)
    await loadUsers()
  }

  async function removeUser(username: string) {
    await deleteUserAccount(username)
    await loadUsers()
    if (sessionUser?.username === username) {
      handleLogout()
    }
  }

  async function updateUser(username: string, updates: UserUpdatePayload) {
    await updateUserAccount(username, updates)
    await loadUsers()
  }

  if (!sessionUser) {
    return (
      <div className="app">
        <Login onLogin={handleLogin} users={userOptions} loading={loadingUsers} />
      </div>
    )
  }

  return (
    <TrackerProvider
      key={sessionUser.username}
      user={sessionUser.username}
      profile={extractDemographics(userOptions, sessionUser.username)}
    >
      <AppShell
        onLogout={handleLogout}
        username={sessionUser.username}
        role={sessionUser.role}
        refreshUsers={loadUsers}
        users={userOptions}
        onCreateUser={addUser}
        onDeleteUser={removeUser}
        onUpdateUser={updateUser}
      />
    </TrackerProvider>
  )
}

function AppShell({
  onLogout,
  username,
  role,
  refreshUsers,
  users,
  onCreateUser,
  onDeleteUser,
  onUpdateUser,
}: {
  onLogout: () => void
  username: string
  role: UserRole
  refreshUsers: () => void
  users: UserAccount[]
  onCreateUser: (user: UserPayload) => Promise<void>
  onDeleteUser: (username: string) => Promise<void>
  onUpdateUser: (username: string, updates: UserUpdatePayload) => Promise<void>
}) {
  const [route, setRoute] = useState<Route>({ name: 'home' })
  const tracker = useTracker()

  if (tracker.loading) {
    return (
      <div className="app">
        <div className="status info">Loading tracker data…</div>
      </div>
    )
  }

  return (
    <div className="app">
      {(tracker.syncing || tracker.error) && (
        <div className={`status ${tracker.error ? 'error' : 'info'}`}>
          {tracker.error ? tracker.error : 'Syncing with tracker storage…'}
        </div>
      )}

      <div className="header">
        <h2>My Tracker</h2>
        <div className="muted">Logged in as {username} ({role})</div>
        <div className="header-actions">
          {route.name !== 'home' && (
            <button className="button small" onClick={() => setRoute({ name: 'home' })}>
              Back
            </button>
          )}
          <button className="button small secondary" onClick={() => tracker.refresh()}>
            Refresh
          </button>
        </div>
      </div>

      {route.name === 'home' && (
        <Home
          disabled={tracker.syncing}
          onChoose={() => setRoute({ name: 'logger' })}
          onHistory={() => setRoute({ name: 'history' })}
          onSettings={() => setRoute({ name: 'settings' })}
          onRefresh={() => tracker.refresh()}
          onChat={() => setRoute({ name: 'chat' })}
        />
      )}

      {route.name === 'logger' && <Logger onDone={() => setRoute({ name: 'home' })} onRefresh={() => tracker.refresh()} />}

      {route.name === 'history' && <History onRefresh={() => tracker.refresh()} />}

      {route.name === 'settings' && (
        <Settings
          onLogout={onLogout}
          username={username}
          role={role}
          onPasswordChanged={refreshUsers}
          users={users}
          onCreateUser={onCreateUser}
          onDeleteUser={onDeleteUser}
          onUpdateUser={onUpdateUser}
        />
      )}

      {route.name === 'chat' && <Chat onDone={() => setRoute({ name: 'home' })} />}
    </div>
  )
}

function Login({
  onLogin,
  users,
  loading,
}: {
  onLogin: (user: SessionUser) => void
  users: UserAccount[]
  loading: boolean
}) {
  const [selected, setSelected] = useState(users[0]?.username ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string>()
  const [appleError, setAppleError] = useState<string>()
  const [expectedAppleEmail, setExpectedAppleEmailState] = useState<string | null>(null)
  const [applePending, setApplePending] = useState(false)
  const appleWindowRef = React.useRef<Window | null>(null)
  const expectedAppleEmailRef = React.useRef<string | null>(null)
  const [googleError, setGoogleError] = useState<string>()
  const [expectedGoogleEmail, setExpectedGoogleEmailState] = useState<string | null>(null)
  const [googlePending, setGooglePending] = useState(false)
  const googleWindowRef = React.useRef<Window | null>(null)
  const expectedGoogleEmailRef = React.useRef<string | null>(null)
  const usersRef = React.useRef<UserAccount[]>(users)
  const onLoginRef = React.useRef(onLogin)
  const appleOrigin = React.useMemo(() => {
    try {
      return APPLE_LOGIN_URL ? new URL(APPLE_LOGIN_URL).origin : ''
    } catch {
      return ''
    }
  }, [])
  const googleOrigin = React.useMemo(() => {
    try {
      return GOOGLE_LOGIN_URL ? new URL(GOOGLE_LOGIN_URL).origin : ''
    } catch {
      return ''
    }
  }, [])
  const selectedUser = users.find(u => u.username === selected)
  const appleUsername = selectedUser?.appleUser?.trim() ?? ''
  const appleAvailable = Boolean(appleUsername && APPLE_LOGIN_URL)
  const setExpectedAppleEmail = React.useCallback((value: string | null) => {
    expectedAppleEmailRef.current = value
    setExpectedAppleEmailState(value)
  }, [])
  const googleUsername = selectedUser?.googleUser?.trim() ?? ''
  const googleAvailable = Boolean(googleUsername && GOOGLE_LOGIN_URL)
  const setExpectedGoogleEmail = React.useCallback((value: string | null) => {
    expectedGoogleEmailRef.current = value
    setExpectedGoogleEmailState(value)
  }, [])

  React.useEffect(() => {
    usersRef.current = users
  }, [users])

  React.useEffect(() => {
    onLoginRef.current = onLogin
  }, [onLogin])

  React.useEffect(() => {
    if (!users.find(u => u.username === selected)) {
      setSelected(users[0]?.username ?? '')
    }
  }, [users, selected])

  React.useEffect(() => {
    expectedAppleEmailRef.current = expectedAppleEmail
  }, [expectedAppleEmail])

  React.useEffect(() => {
    expectedGoogleEmailRef.current = expectedGoogleEmail
  }, [expectedGoogleEmail])

  React.useEffect(() => {
    if (!appleOrigin) {
      return
    }
    function handleAppleMessage(event: MessageEvent) {
      if (appleOrigin && event.origin !== appleOrigin) {
        return
      }
      const payload = event.data
      if (!payload || payload.source !== 'tracker.apple') {
        return
      }
      const awaitingEmail = expectedAppleEmailRef.current
      if (!awaitingEmail) {
        console.log('RECEIVED: Apple login payload but no pending request; ignoring.')
        return
      }
      const details = payload.data || {}
      const resolvedEmail = normalizeEmail(details.email || details?.state?.email)
      console.log('RECEIVED: Apple login payload for', resolvedEmail || '(unknown)', details)
      if (!resolvedEmail) {
        setAppleError('Apple login did not include an email address.')
        setExpectedAppleEmail(null)
        setApplePending(false)
        console.warn('RECEIVED: Apple login payload missing email', details)
        return
      }
      const match = usersRef.current.find(u => normalizeEmail(u.appleUser || '') === resolvedEmail)
      if (!match) {
        setAppleError('Apple ID is not linked to any user.')
        setExpectedAppleEmail(null)
        setApplePending(false)
        console.warn('RECEIVED: Apple login payload with unmatched email', resolvedEmail, usersRef.current)
        return
      }
      appleWindowRef.current?.close()
      appleWindowRef.current = null
      setAppleError(undefined)
      setExpectedAppleEmail(null)
      setApplePending(false)
      console.log('RECEIVED: Apple login matched user', match.username, 'logging in.')
      onLoginRef.current?.({ username: match.username, role: match.role })
    }
    window.addEventListener('message', handleAppleMessage)
    return () => window.removeEventListener('message', handleAppleMessage)
  }, [appleOrigin])

  React.useEffect(() => {
    if (!googleOrigin) {
      return
    }
    function handleGoogleMessage(event: MessageEvent) {
      if (googleOrigin && event.origin !== googleOrigin) {
        return
      }
      const payload = event.data
      if (!payload || payload.source !== 'tracker.google') {
        return
      }
      const awaitingEmail = expectedGoogleEmailRef.current
      if (!awaitingEmail) {
        console.log('RECEIVED: Google login payload but no pending request; ignoring.')
        return
      }
      const details = payload.data || {}
      const resolvedEmail = normalizeEmail(details.email || details?.state?.email)
      console.log('RECEIVED: Google login payload for', resolvedEmail || '(unknown)', details)
      if (!resolvedEmail) {
        setGoogleError('Google login did not include an email address.')
        setExpectedGoogleEmail(null)
        setGooglePending(false)
        console.warn('RECEIVED: Google login payload missing email', details)
        return
      }
      const match = usersRef.current.find(u => normalizeEmail(u.googleUser || u['google-user'] || '') === resolvedEmail)
      if (!match) {
        setGoogleError('Google account is not linked to any user.')
        setExpectedGoogleEmail(null)
        setGooglePending(false)
        console.warn('RECEIVED: Google login payload with unmatched email', resolvedEmail, usersRef.current)
        return
      }
      googleWindowRef.current?.close()
      googleWindowRef.current = null
      setGoogleError(undefined)
      setExpectedGoogleEmail(null)
      setGooglePending(false)
      console.log('RECEIVED: Google login matched user', match.username, 'logging in.')
      onLoginRef.current?.({ username: match.username, role: match.role })
    }
    window.addEventListener('message', handleGoogleMessage)
    return () => window.removeEventListener('message', handleGoogleMessage)
  }, [googleOrigin])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    console.log('PRESSED: Login button for user', selected)
    const found = users.find(u => u.username === selected)
    if (!found) {
      setError('Select a user')
      return
    }
    const isValid = await verifyPassword(found, password)
    if (!isValid) {
      setError('Incorrect password')
      return
    }
    setError(undefined)
    onLogin({ username: found.username, role: found.role })
    setPassword('')
  }

  function startAppleLogin() {
    if (!appleAvailable) {
      setAppleError('Apple login is not available for this user.')
      return
    }
    console.log('PRESSED: Sign in with Apple for', appleUsername || selected)
    if (typeof window === 'undefined') {
      return
    }
    setAppleError(undefined)
    setApplePending(true)
    const normalizedEmail = normalizeEmail(appleUsername)
    setExpectedAppleEmail(normalizedEmail)
    try {
      const url = new URL(APPLE_LOGIN_URL)
      if (appleUsername) {
        url.searchParams.set('user', appleUsername)
      }
      if (typeof window !== 'undefined') {
        url.searchParams.set('redirect', window.location.origin)
      }
      url.searchParams.set('debug', '0')
      url.searchParams.set('ts', Date.now().toString())
      console.log('SENT: Opening Apple login URL', url.toString())
      const popup = window.open(url.toString(), 'tracker-apple-login', 'width=480,height=640')
      if (!popup) {
        setAppleError('Popup blocked. Please allow popups for this site and try again.')
        setApplePending(false)
        setExpectedAppleEmail(null)
        return
      }
      appleWindowRef.current = popup
      if (typeof popup.focus === 'function') {
        try {
          popup.focus()
        } catch (focusErr) {
          console.warn('Popup focus failed', focusErr)
        }
      }
    } catch (err) {
      console.error(err)
      setAppleError('Apple login configuration is invalid.')
      setApplePending(false)
      setExpectedAppleEmail(null)
    }
  }

  function startGoogleLogin() {
    if (!googleAvailable) {
      setGoogleError('Google login is not available for this user.')
      return
    }
    console.log('PRESSED: Sign in with Google for', googleUsername || selected)
    if (typeof window === 'undefined') {
      return
    }
    setGoogleError(undefined)
    setGooglePending(true)
    const normalizedEmail = normalizeEmail(googleUsername)
    setExpectedGoogleEmail(normalizedEmail)
    try {
      const url = new URL(GOOGLE_LOGIN_URL)
      if (googleUsername) {
        url.searchParams.set('user', googleUsername)
      }
      if (typeof window !== 'undefined') {
        url.searchParams.set('redirect', window.location.origin)
      }
      url.searchParams.set('debug', '0')
      url.searchParams.set('ts', Date.now().toString())
      console.log('SENT: Opening Google login URL', url.toString())
      const popup = window.open(url.toString(), 'tracker-google-login', 'width=480,height=640')
      if (!popup) {
        setGoogleError('Popup blocked. Please allow popups for this site and try again.')
        setGooglePending(false)
        setExpectedGoogleEmail(null)
        return
      }
      googleWindowRef.current = popup
      if (typeof popup.focus === 'function') {
        try {
          popup.focus()
        } catch (focusErr) {
          console.warn('Popup focus failed', focusErr)
        }
      }
    } catch (err) {
      console.error(err)
      setGoogleError('Google login configuration is invalid.')
      setGooglePending(false)
      setExpectedGoogleEmail(null)
    }
  }

  return (
    <div className="auth-card">
      <h2>Sign in</h2>
      <form onSubmit={submit}>
        <label>
          User
          <select value={selected} onChange={e => setSelected(e.target.value)} disabled={loading}>
            {users.map(u => (
              <option key={u.username} value={u.username}>
                {u.username}
              </option>
            ))}
          </select>
        </label>
        <label style={{ marginTop: 12 }}>
          Password
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password"
          />
        </label>
        {error && <div className="status error" style={{ marginTop: 12 }}>{error}</div>}
        {appleAvailable && (
          <button
            type="button"
            className="button secondary"
            style={{ width: '100%', marginTop: 12 }}
            onClick={startAppleLogin}
            disabled={applePending}
          >
            {applePending ? 'Waiting for Apple…' : 'Sign in with Apple'}
          </button>
        )}
        {appleError && <div className="status error" style={{ marginTop: 12 }}>{appleError}</div>}
        {googleAvailable && (
          <button
            type="button"
            className="button secondary"
            style={{ width: '100%', marginTop: 12 }}
            onClick={startGoogleLogin}
            disabled={googlePending}
          >
            {googlePending ? 'Waiting for Google…' : 'Sign in with Google'}
          </button>
        )}
        {googleError && <div className="status error" style={{ marginTop: 12 }}>{googleError}</div>}
        <button type="submit" className="button" style={{ width: '100%', marginTop: 16 }}>
          Log in
        </button>
      </form>
    </div>
  )
}

async function verifyPassword(user: UserAccount, candidate: string) {
  const trimmed = (candidate || '').trim()
  if (typeof user.passwordHash === 'string') {
    if (user.passwordHash === '') {
      return trimmed === ''
    }
    if (!trimmed) {
      return false
    }
    const hashedInput = await hashPassword(trimmed)
    return hashedInput === user.passwordHash
  }
  if (typeof user.password === 'string') {
    return trimmed === user.password
  }
  return trimmed === ''
}

function sortUsersByRecency(users: UserAccount[]): UserAccount[] {
  return [...users].sort((a, b) => {
    const aTime = parseTimestamp(a.lastLogin)
    const bTime = parseTimestamp(b.lastLogin)
    if (aTime && bTime && aTime !== bTime) {
      return bTime - aTime
    }
    if (aTime && !bTime) {
      return -1
    }
    if (!aTime && bTime) {
      return 1
    }
    return a.username.localeCompare(b.username, undefined, { sensitivity: 'base' })
  })
}

function extractDemographics(users: UserAccount[], username: string): UserDemographics | undefined {
  const match = users.find(u => u.username === username)
  if (!match) return undefined
  return {
    age: match.age,
    sex: match.sex,
  }
}

function parseTimestamp(value?: string): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

function normalizeEmail(value?: string | null) {
  return (value || '').trim().toLowerCase()
}
